/**
 * Migración Flujo v2 — UI admin.
 *
 * Permite ver el preview de la migración (qué cambiaría sin tocar BD) y aplicarlo
 * con doble confirmación. Sustituye al script CLI `scripts/migrar_estados_v2.py`
 * para que el usuario no tenga que abrir terminal en producción.
 *
 * Decisión Fernando 31/05/26 — Opción A "Migración Total".
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, ShieldCheck, AlertTriangle, RefreshCcw, CheckCircle2, FileText } from "lucide-react";

export default function MigracionFlujoV2() {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [confirmStep, setConfirmStep] = useState(0); // 0=ver preview, 1=ok 1ª conf, 2=ok 2ª conf (ejecuta)

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/migracion/flujo-v2/preview");
      setPreview(data);
    } catch (e) {
      alert("Error cargando preview: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const aplicar = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post("/migracion/flujo-v2/aplicar", { confirmar: true });
      setResultado(data);
      setConfirmStep(0);
      await cargar();
    } catch (e) {
      alert("Error aplicando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAplicando(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      <PageHeader
        icon={RefreshCcw}
        title="Migración Flujo v2"
        description="Mapea los estados/orígenes legacy a los oficiales. Decisión firmada 31/05/26."
      />

      {/* Aviso */}
      <div className="mt-4 bg-amber-500/5 border border-amber-500/30 rounded-lg p-4" data-testid="migracion-aviso">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-100">
            <p className="font-semibold">Esta acción modifica registros en la base de datos.</p>
            <p className="text-amber-300/80 mt-1">
              Primero revisa el preview de abajo. Si todo cuadra, pulsa <b>Aplicar</b> — pedirá doble confirmación.
              Recordatorio: Fernando dijo que migraría a mano los datos de prueba en producción ANTES de ejecutar esto.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </div>
      ) : preview ? (
        <>
          {/* Resumen general */}
          <div className="mt-6 grid grid-cols-3 gap-3" data-testid="migracion-resumen">
            <CardStat label="Peticiones" total={preview.peticiones.total} color="text-amber-300" testid="stat-peticiones" />
            <CardStat label="Líneas" total={preview.lineas.total} color="text-blue-300" testid="stat-lineas" />
            <CardStat label="Componentes" total={preview.componentes.total} color="text-emerald-300" testid="stat-componentes" />
          </div>

          {/* Resumen mapeo por tabla */}
          <Section titulo="📋 Peticiones" total={preview.peticiones.total} testid="sec-peticiones">
            <ResumenTable filas={preview.peticiones.resumen} />
            <MuestrasTable
              filas={preview.peticiones.muestras}
              columnas={[
                { key: "codigo", label: "Código" },
                { key: "nombre", label: "Nombre" },
                { key: "viejo", label: "Estado viejo" },
                { key: "nuevo", label: "Estado nuevo" },
              ]}
            />
          </Section>

          <Section titulo="🔧 Líneas" total={preview.lineas.total} testid="sec-lineas">
            <ResumenTable filas={preview.lineas.resumen} />
            <MuestrasTable
              filas={preview.lineas.muestras}
              columnas={[
                { key: "descripcion", label: "Descripción" },
                { key: "viejo", label: "Tipo viejo" },
                { key: "nuevo", label: "Tipo nuevo" },
              ]}
            />
          </Section>

          <Section titulo="🧩 Componentes" total={preview.componentes.total} testid="sec-componentes">
            <div className="mb-2 text-xs text-zinc-400">Cambios de estado:</div>
            <ResumenTable filas={preview.componentes.resumen_estado} />
            <div className="mt-3 mb-2 text-xs text-zinc-400">Cambios de origen:</div>
            <ResumenTable filas={preview.componentes.resumen_origen} />
            <MuestrasTable
              filas={preview.componentes.muestras}
              columnas={[
                { key: "nombre", label: "Nombre" },
                { key: "estado_viejo", label: "Estado viejo" },
                { key: "estado_nuevo", label: "Estado nuevo" },
                { key: "origen_viejo", label: "Origen viejo" },
                { key: "origen_nuevo", label: "Origen nuevo" },
              ]}
            />
          </Section>

          {/* Botones de acción */}
          <div className="mt-8 sticky bottom-4 bg-zinc-950/95 backdrop-blur border border-zinc-800 rounded-lg p-4 flex items-center justify-between"
               data-testid="migracion-actions">
            <div className="text-sm text-zinc-300">
              <span className="font-semibold text-amber-300">{preview.total_general}</span> cambios en total ·{" "}
              {confirmStep === 0 && <span className="text-zinc-500">¿Aplicar?</span>}
              {confirmStep === 1 && <span className="text-amber-200">⚠️ Pulsa otra vez para confirmar</span>}
              {confirmStep === 2 && <span className="text-emerald-400">Aplicando…</span>}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={cargar}
                disabled={aplicando}
                data-testid="btn-recargar-preview"
                className="border-zinc-700"
              >
                <RefreshCcw className="w-4 h-4 mr-1" /> Recargar
              </Button>
              {confirmStep === 0 && preview.total_general > 0 && (
                <Button
                  onClick={() => setConfirmStep(1)}
                  data-testid="btn-aplicar-1"
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                >
                  Aplicar migración <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {confirmStep === 1 && (
                <Button
                  onClick={() => { setConfirmStep(2); aplicar(); }}
                  disabled={aplicando}
                  data-testid="btn-aplicar-2"
                  className="bg-red-500 hover:bg-red-400 text-white font-bold"
                >
                  <ShieldCheck className="w-4 h-4 mr-1" /> SÍ, APLICAR DE VERDAD
                </Button>
              )}
              {confirmStep === 2 && (
                <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
              )}
            </div>
          </div>
        </>
      ) : null}

      {/* Resultado */}
      {resultado && (
        <div className="mt-6 bg-emerald-500/5 border border-emerald-500/40 rounded-lg p-4" data-testid="migracion-resultado">
          <div className="flex items-center gap-2 text-emerald-300 font-semibold mb-2">
            <CheckCircle2 className="w-5 h-5" /> Migración aplicada
          </div>
          <pre className="text-xs text-zinc-300 bg-zinc-950 p-3 rounded overflow-auto max-h-72">
            {JSON.stringify(resultado.resultados, null, 2)}
          </pre>
        </div>
      )}

      {/* Vínculo al documento de decisión */}
      <div className="mt-6 text-xs text-zinc-500">
        <FileText className="inline w-3 h-3 mr-1" />
        Documento de referencia: <a href="/docs-internos" className="text-amber-300 hover:underline">Inventario de Estados HOY</a> (firmado 31/05/26).
      </div>
    </div>
  );
}

const CardStat = ({ label, total, color, testid }) => (
  <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4" data-testid={testid}>
    <div className="text-xs text-zinc-400 uppercase tracking-wide">{label}</div>
    <div className={`text-3xl font-bold mt-1 ${color}`}>{total}</div>
    <div className="text-[10px] text-zinc-600 mt-0.5">a migrar</div>
  </div>
);

const Section = ({ titulo, total, children, testid }) => (
  <div className="mt-6 bg-zinc-900/30 border border-zinc-800 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-baseline justify-between mb-3">
      <h3 className="text-amber-300 font-semibold">{titulo}</h3>
      <div className="text-xs text-zinc-500">{total} cambios</div>
    </div>
    {total === 0 ? (
      <div className="text-xs text-zinc-500 italic py-2">Nada que migrar</div>
    ) : children}
  </div>
);

const ResumenTable = ({ filas }) => filas.length === 0 ? null : (
  <table className="w-full text-xs">
    <tbody>
      {filas.map((f, i) => (
        <tr key={i} className="border-b border-zinc-800 last:border-0">
          <td className="py-1.5 text-zinc-300 font-mono">{f.viejo}</td>
          <td className="py-1.5 text-zinc-500 w-12 text-center">→</td>
          <td className="py-1.5 text-emerald-300 font-mono">{f.nuevo}</td>
          <td className="py-1.5 text-right text-amber-300 font-semibold">{f.count} registro{f.count !== 1 ? "s" : ""}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const MuestrasTable = ({ filas, columnas }) => filas.length === 0 ? null : (
  <details className="mt-3 text-xs">
    <summary className="text-zinc-500 cursor-pointer hover:text-zinc-300">
      Ver muestra de registros ({filas.length})
    </summary>
    <div className="mt-2 overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-zinc-500 border-b border-zinc-800">
            {columnas.map((c) => (
              <th key={c.key} className="text-left py-1 px-2 font-normal">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
              {columnas.map((c) => (
                <td key={c.key} className="py-1 px-2 text-zinc-300 font-mono">
                  {Array.isArray(f[c.key]) ? f[c.key].join(", ") : (f[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </details>
);
